import { useQuery } from '@tanstack/react-query'
import { aheadQuery, type AheadBattery, type AheadCapacity, type AheadCert, type AheadDisk, type AheadRestart } from '../api/overview'
import { Content } from '../ui/Shell'
import { convertBytes, phpDay } from './format'
import { hrefOf } from './href'

/**
 * Overview::ahead - macheta today/ahead.php, cele sase liste.
 * GET /api/v2/overview/ahead. AmCharts nu intra. Legaturile moarte
 * Classic nu se deseneaza ca <a>.
 */

const AHEAD_STYLE = `
.od-ahead-h { margin: 22px 0 10px; font-size: 13px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; color: var(--od-ink-faint, #6b7480); }
.od-ahead-h:first-child { margin-top: 4px; }
.od-today { padding: 4px 4px 32px; max-width: 900px; }
.od-today-lead { margin: 0 0 18px; font-size: 15px; color: var(--od-ink-soft, #5b6672); }
.od-today-lead b { color: var(--od-ink, #1f2933); font-size: 17px; }
.od-today-unknown { display: block; margin-top: 6px; font-size: 13px; color: var(--od-warn, #9e6b00); }
.od-today-clear { display: flex; gap: 14px; align-items: flex-start; padding: 18px 20px; border-radius: 6px; background: var(--od-ok-bg, #eef8f1); color: var(--od-ink, #1f2933); }
.od-today-clear i { font-size: 22px; color: var(--od-ok, #1f7a3f); margin-top: 1px; }
.od-today-rows { display: flex; flex-direction: column; gap: 8px; }
.od-today-row { display: flex; align-items: center; gap: 14px; padding: 12px 14px; border-radius: 6px; background: #fff; border: 1px solid rgba(0, 0, 0, .08); border-left-width: 4px; color: var(--od-ink, #1f2933); text-decoration: none; }
.od-today-row:hover { text-decoration: none; background: rgba(0, 0, 0, .02); color: var(--od-ink, #1f2933); }
.od-today-icon { width: 30px; text-align: center; color: var(--od-ink-faint, #6b7480); font-size: 16px; }
.od-today-n { min-width: 72px; font-size: 22px; font-weight: 700; text-align: right; }
.od-ahead-n { font-size: 16px; min-width: 92px; }
.od-today-text { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.od-today-label { font-weight: 700; }
.od-ahead-mount { display: inline-block; margin-left: 6px; padding: 0 6px; font-family: Menlo, Consolas, monospace; font-size: 12px; font-weight: 500; background: rgba(0, 0, 0, .06); border-radius: 3px; }
.od-today-about { font-size: 13px; color: var(--od-ink-soft, #5b6672); }
.od-today-go { color: var(--od-ink-faint, #6b7480); }
.od-today-row.is-crit { border-left-color: var(--od-crit, #b42318); }
.od-today-row.is-crit .od-today-n { color: var(--od-crit, #b42318); }
.od-today-row.is-warn { border-left-color: var(--od-warn, #9e6b00); }
.od-today-row.is-warn .od-today-n { color: var(--od-warn, #9e6b00); }
.od-today-row.is-info { border-left-color: var(--od-info, #1f62cb); }
.od-today-row.is-info .od-today-n { color: var(--od-info, #1f62cb); }
`

export function diskTone(hours: number): string {
  return hours <= 24 ? 'crit' : hours <= 72 ? 'warn' : 'info'
}

export function lineTone(days: number): string {
  return days <= 7 ? 'crit' : days <= 14 ? 'warn' : 'info'
}

function Unknown({ text }: { text: string }) {
  return (
    <p className="od-today-lead">
      <span className="od-today-unknown">{text}</span>
    </p>
  )
}

function Clear({ title, body }: { title: string; body: string }) {
  return (
    <div className="od-today-clear">
      <i className="fas fa-check-circle" aria-hidden="true"></i>
      <div>
        <b>{title}</b> {body}
      </div>
    </div>
  )
}

function Row({
  href,
  tone,
  icon,
  when,
  label,
  extra,
  on,
  about,
}: {
  href: string | null
  tone: string
  icon: string
  when: string
  label: string
  /** Punctul de montare al discului: pastila monospatiata, ca in macheta. */
  extra?: string
  /**
   * Masina pe care sta lucrul din `label` - la certificate, unde eticheta e
   * subiectul certificatului, nu numele masinii.
   *
   * Macheta veche scria "on NUME" cu litere sterse, langa subiect. Rescrierea
   * il trecea prin `extra`, adica prin pastila monospatiata a punctelor de
   * montare - un nume de masina desenat ca o cale de disc, si fara cuvantul
   * care spunea ce relatie e intre ele.
   */
  on?: string
  about: string
}) {
  const inner = (
    <>
      <span className="od-today-icon">
        <i className={`fas ${icon}`} aria-hidden="true"></i>
      </span>
      <span className="od-today-n od-ahead-n">{when}</span>
      <span className="od-today-text">
        <span className="od-today-label">
          {label}
          {extra ? <span className="od-ahead-mount">{extra}</span> : null}
          {on ? <span className="od-today-about"> on {on}</span> : null}
        </span>
        <span className="od-today-about">{about}</span>
      </span>
      <i className="fas fa-chevron-right od-today-go" aria-hidden="true"></i>
    </>
  )
  const cls = `od-today-row is-${tone}`
  if (!href) return <div className={cls}>{inner}</div>
  return (
    <a className={cls} href={href}>
      {inner}
    </a>
  )
}

export function AheadPage() {
  const q = useQuery(aheadQuery)
  const filling = q.data ? q.data.filling : null
  const memory = q.data ? q.data.memory : null
  const cpu = q.data ? q.data.cpu : null
  const batteries = q.data ? q.data.batteries : null
  const restarts = q.data ? q.data.restarts : null
  const certificates = q.data ? q.data.certificates : null

  return (
    <Content>
      <style>{AHEAD_STYLE}</style>
      <div className="od-today od-ahead">
        <h3 className="od-ahead-h">Disks</h3>
        {filling === null ? (
          <Unknown text='The disk samples could not be read just now - that is not the same as "nothing is filling".' />
        ) : filling.length === 0 ? (
          <Clear title="Nothing is filling up." body="No disk on your machines reaches full inside 7 days at its current rate." />
        ) : (
          <>
            <p className="od-today-lead">
              <b>{filling.length}</b> disk{filling.length === 1 ? '' : 's'} will be full inside a week if nothing changes.
              The sooner ones are first. Open the machine to see the finer trend and what is growing.
            </p>
            <div className="od-today-rows">
              {filling.map((row: AheadDisk) => (
                <Row
                  key={`${row.device_id}-${row.mount}`}
                  href={hrefOf(row.url || `opteemum/deviceHddDetails/${row.device_id}`)}
                  tone={diskTone(row.hours)}
                  icon="fa-hdd"
                  when={row.when}
                  label={row.name}
                  extra={row.mount}
                  about={`${row.used_pct !== null ? `${row.used_pct}% used · ` : ''}${convertBytes(row.free ?? 0)} free of ${convertBytes(row.total ?? 0)} · growing ${row.rate ?? ''}`}
                />
              ))}
            </div>
          </>
        )}

        <h3 className="od-ahead-h">Memory</h3>
        {memory === null ? (
          <Unknown text="The daily memory history could not be read just now." />
        ) : memory.length === 0 ? (
          <Clear
            title="No memory is creeping up."
            body="No machine's daily average heads to 90% inside 30 days on its 30-day line. Leaks show up here weeks before the alarm."
          />
        ) : (
          <>
            <p className="od-today-lead">
              <b>{memory.length}</b> machine{memory.length === 1 ? '' : 's'} will reach 90% memory inside a month if the line holds.
              A straight line up, day after day, is a leak in a service - restart it on your terms, not at 3 a.m.
            </p>
            <CapacityRows rows={memory} />
          </>
        )}

        <h3 className="od-ahead-h">Processor</h3>
        {cpu === null ? (
          <Unknown text="The daily processor history could not be read just now." />
        ) : cpu.length === 0 ? (
          <Clear
            title="No processor is running out of headroom."
            body="No machine's daily average load heads to 85% inside 30 days on its 30-day line. Growth shows up here before the peaks start to hurt."
          />
        ) : (
          <>
            <p className="od-today-lead">
              <b>{cpu.length}</b> machine{cpu.length === 1 ? '' : 's'} will average 85% processor load inside a month if the line holds.
              At that level there is no room left for the peaks - plan the upgrade, the move or the split now.
            </p>
            <CapacityRows rows={cpu} chip />
          </>
        )}

        <h3 className="od-ahead-h">Batteries</h3>
        {batteries === null ? (
          <Unknown text="The battery inventory could not be read just now." />
        ) : batteries.length === 0 ? (
          <Clear
            title="No battery is worn out."
            body="Every laptop battery still holds 80% or more of its factory capacity. Agents report it from 26.9.5.9 - a laptop that never appears here has not reported yet."
          />
        ) : (
          <>
            <p className="od-today-lead">
              <b>{batteries.length}</b> laptop batter{batteries.length === 1 ? 'y' : 'ies'} hold{batteries.length === 1 ? 's' : ''} under 80% of factory capacity.
              Order the replacement while the machine still gets through a meeting - not the day it dies at lunch.
            </p>
            <div className="od-today-rows">
              {batteries.map((row: AheadBattery) => (
                <Row
                  key={row.device_id}
                  href={hrefOf(`inventoree/deviceDetails/${row.device_id}`)}
                  tone={row.health < 60 ? 'crit' : row.health < 70 ? 'warn' : 'info'}
                  icon="fa-battery-quarter"
                  when={`${row.health}%`}
                  label={row.name}
                  about={`of factory capacity${row.cycles !== null ? ` · ${row.cycles} charge cycles` : ''}${row.condition !== '' ? ` · ${row.condition}` : ''}`}
                />
              ))}
            </div>
          </>
        )}

        <h3 className="od-ahead-h">Restarts waiting</h3>
        {restarts === null ? (
          <Unknown text="The pending-restart list could not be read just now." />
        ) : restarts.length === 0 ? (
          <Clear
            title="No restart has been waiting for a week."
            body="Updates that need a reboot get one within seven days everywhere. A machine that keeps postponing shows up here, with the count."
          />
        ) : (
          <>
            <p className="od-today-lead">
              <b>{restarts.length}</b> machine{restarts.length === 1 ? '' : 's'} ha{restarts.length === 1 ? 's' : 've'} been waiting a week or more to restart.
              Installed updates that are never applied protect nobody - pick a window and reboot them.
            </p>
            <div className="od-today-rows">
              {restarts.map((row: AheadRestart) => (
                <Row
                  key={row.device_id}
                  href={hrefOf(`proactivitee/deviceSummary/${row.device_id}`)}
                  tone={row.days >= 30 ? 'crit' : row.days >= 14 ? 'warn' : 'info'}
                  icon="fa-power-off"
                  when={`${row.days} d`}
                  label={row.name}
                  about={`restart pending since ${phpDay(row.since)}${row.tip === 3 ? ' · server - plan the window' : ''}`}
                />
              ))}
            </div>
          </>
        )}

        <h3 className="od-ahead-h">Certificates expiring</h3>
        {certificates === null ? (
          <Unknown text="The certificate list could not be read just now." />
        ) : certificates.length === 0 ? (
          <Clear
            title="No certificate expires in the next 30 days."
            body="The agents check every TLS port a machine listens on and its certificate stores - web, LDAPS, RDP, mail, WinRM. Whatever comes within a month shows up here first."
          />
        ) : (
          <>
            <p className="od-today-lead">
              <b>{certificates.length}</b> certificate{certificates.length === 1 ? '' : 's'} expire{certificates.length === 1 ? 's' : ''} within 30 days or already ha{certificates.length === 1 ? 's' : 've'}.
              An expired certificate on a domain controller or mail server stops logins and mail - renew before the date, not after the calls.
            </p>
            <div className="od-today-rows">
              {certificates.map((row: AheadCert) => (
                <Row
                  key={`${row.device_id}-${row.subject}`}
                  href={hrefOf(`proactivitee/deviceSummary/${row.device_id}`)}
                  tone={lineTone(row.days)}
                  icon="fa-certificate"
                  when={row.days < 0 ? 'expired' : `${row.days} d`}
                  label={row.subject}
                  on={row.name}
                  about={`${row.days < 0 ? 'expired' : 'expires'} ${phpDay(row.not_after, true)}${row.issuer !== '' ? ` · ${row.issuer}` : ''}${row.sources !== '' ? ` · ${row.sources}` : ''}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </Content>
  )
}

function CapacityRows({ rows, chip = false }: { rows: AheadCapacity[]; chip?: boolean }) {
  return (
    <div className="od-today-rows">
      {rows.map((row) => (
        <Row
          key={row.device_id}
          href={hrefOf(`proactivitee/performanceSummary/${row.device_id}`)}
          tone={lineTone(row.days)}
          icon={chip ? 'fa-microchip' : 'fa-memory'}
          when={row.when}
          label={row.name}
          about={`${row.now_pct}% ${chip ? 'average load on the line today' : 'used on the line today'} · climbing ${row.per_day} points a day · ${row.points} days of history`}
        />
      ))}
    </div>
  )
}
